import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet'; // Add this import
import { useOrder } from '../contexts/OrderContext';
import styles from '../styles/DetailsPage.module.css';

const DetailsPage = () => {
  const navigate = useNavigate();
  const { currentOrder, updateOrderDetails } = useOrder();
  const [showNotification, setShowNotification] = useState(true);

  const [formData, setFormData] = useState({
    occasion: '',
    recipientName: '',
    rushDelivery: false,
    songStory: '',
    keyPoints: '',
    songTone: '',
    vocalPreference: 'no-preference', // Default value
    favoriteArtists: '',
    additionalNotes: ''
  });

  const hasRequiredFields = formData.occasion && formData.recipientName && formData.songStory;
  const hasSongPreferences = formData.songTone && formData.vocalPreference !== '';
  const isFormComplete = hasRequiredFields && hasSongPreferences;

  useEffect(() => {
    if (currentOrder.details) {
      setFormData(prevData => ({
        ...prevData,
        ...currentOrder.details
      }));
    }
    setShowNotification(true);
    window.scrollTo(0, 0);
  }, [currentOrder.details]);

  // Debug state updates in Safari
  useEffect(() => {
    console.log('formData:', formData);
    console.log('hasRequiredFields:', hasRequiredFields, {
      occasion: formData.occasion,
      recipientName: formData.recipientName,
      songStory: formData.songStory
    });
    console.log('hasSongPreferences:', hasSongPreferences, {
      songTone: formData.songTone,
      vocalPreference: formData.vocalPreference
    });
    console.log('isFormComplete:', isFormComplete);
  }, [formData, hasRequiredFields, hasSongPreferences, isFormComplete]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    console.log(`Input changed: ${name} = ${value || checked}`);
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleBlur = (e) => {
    const { name, value, type, checked } = e.target;
    console.log(`Input blurred: ${name} = ${value || checked}`);
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormComplete) {
      alert('Please fill in all required fields');
      return;
    }
    updateOrderDetails('details', formData);
    navigate('/checkout');
  };

  return (
    <div className={styles.container}>
       <Helmet>
        <title>Tell Us About Your Song | Custom Song Details | MakeMeSongs</title>
        <meta name="description" content="Share your story and preferences to create your perfect custom song. Specify occasion, recipient, delivery timing, vocal preference, and important details for a truly personalized musical gift." />
        <meta name="keywords" content="custom song details, personalized music story, song customization, vocal preferences, music gift details" />
        <meta property="og:title" content="Tell Us About Your Song | MakeMeSongs" />
        <meta property="og:description" content="Share your story, memories, and preferences to create a custom song that's perfectly personalized for your special occasion." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://makemesongs.com/details" />
        <meta property="og:image" content="https://makemesongs.com/og-image.jpg" />
        <link rel="canonical" href="https://makemesongs.com/details" />
      </Helmet>
      <div className={styles.header}>
        <h1 className={styles.title}>🎵 Tell Us About Your Song</h1>
        <p className={styles.subtitle}>
          Share the details for your {currentOrder.package} Package masterpiece!
        </p>
      </div>

      <div className={styles.topNavigation}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate('/genres')}
        >
          Back to Styles
        </button>
        <button
          type="submit"
          form="detailsForm"
          className={styles.nextButton}
          disabled={!isFormComplete}
          onClick={(e) => {
            if (isFormComplete) handleSubmit(e); // Fallback for Safari
          }}
        >
          Continue to Payment
        </button>
      </div>

      <form id="detailsForm" className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.instructionContainer}>
          <p className={styles.instructionText}>
            Please fill in the form below to customize your song. Fields marked with <span className={styles.requiredStar}>*</span> are required.
          </p>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Basic Information</h2>
          <div className={styles.formGroup}>
            <label htmlFor="occasion" className={styles.label}>
              What's the occasion? <span className={styles.requiredStar}>*</span>
            </label>
            <select
              id="occasion"
              name="occasion"
              className={styles.select}
              value={formData.occasion}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
            >
              <option value="">Select an occasion</option>
              <option value="birthday">Birthday</option>
              <option value="anniversary">Anniversary</option>
              <option value="wedding">Wedding</option>
              <option value="retirement">Retirement</option>
              <option value="graduation">Graduation</option>
              <option value="promposal">Promposal</option>
              <option value="proposal">Marriage Proposal</option>
              <option value="valentines">Valentine's Day</option>
              <option value="mother-day">Mother's Day</option>
              <option value="father-day">Father's Day</option>
              <option value="christmas">Christmas</option>
              <option value="baby-shower">Baby Shower</option>
              <option value="gender-reveal">Gender Reveal</option>
              <option value="new-baby">New Baby</option>
              <option value="friendship">Friendship</option>
              <option value="apology">Apology</option>
              <option value="thank-you">Thank You</option>
              <option value="memorial">Memorial/Tribute</option>
              <option value="reunion">Family Reunion</option>
              <option value="dating-anniversary">Dating Anniversary</option>
              <option value="prom">Prom</option>
              <option value="bar-mitzvah">Bar/Bat Mitzvah</option>
              <option value="quinceañera">Quinceañera</option>
              <option value="sweet-sixteen">Sweet Sixteen</option>
              <option value="new-job">New Job</option>
              <option value="promotion">Promotion</option>
              <option value="get-well">Get Well/Feel Better</option>
              <option value="teacher-appreciation">Teacher Appreciation</option>
              <option value="themed-party">Themed Party</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="recipientName" className={styles.label}>
              Who is this song for? <span className={styles.requiredStar}>*</span>
            </label>
            <input
              type="text"
              id="recipientName"
              name="recipientName"
              className={styles.input}
              placeholder="E.g., My wife Martha"
              value={formData.recipientName}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>When do you need this song?</label>
            <div className={styles.radioGroup}>
              <label className={`${styles.radioOption} ${!formData.rushDelivery ? styles.radioSelected : ''}`}>
                <input
                  type="radio"
                  name="rushDelivery"
                  checked={!formData.rushDelivery}
                  onChange={() => setFormData({ ...formData, rushDelivery: false })}
                  onBlur={handleBlur}
                  className={styles.radioInput}
                />
                <span className={styles.radioLabel}>
                  <strong>Standard Delivery</strong><br/>
                  <small>(Based on package)</small>
                </span>
              </label>
              <label className={`${styles.radioOption} ${formData.rushDelivery ? styles.radioSelected : ''}`}>
                <input
                  type="radio"
                  name="rushDelivery"
                  checked={formData.rushDelivery}
                  onChange={() => setFormData({ ...formData, rushDelivery: true })}
                  onBlur={handleBlur}
                  className={styles.radioInput}
                />
                <span className={styles.radioLabel}>
                  <strong>Rush Delivery</strong><br/>
                  <small>(+$30, 3-6 hours)</small>
                </span>
              </label>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Tell Us Your Story</h2>
          <div className={styles.formGroup}>
            <label htmlFor="songStory" className={styles.label}>
              Share your story or the special memories you want included <span className={styles.requiredStar}>*</span>
            </label>
            <textarea
              id="songStory"
              name="songStory"
              className={styles.textarea}
              placeholder="Memories, moments, magic..."
              value={formData.songStory}
              onChange={handleInputChange}
              onBlur={handleBlur}
              rows={6}
              required
            />
            <div className={styles.helpText}>
              This is the most important part! The more details you share, the more personal your song will be.{' '}
              <button
                type="button"
                className={styles.exampleToggle}
                onClick={() => document.getElementById('exampleText').classList.toggle(styles.hidden)}
              >
                See Example
              </button>
            </div>
            <div id="exampleText" className={`${styles.exampleText} ${styles.hidden}`}>
              "Martha and I met at a garden center 40 years ago. She was buying roses, and I was there getting soil for my tomato plants. We struck up a conversation about gardening, and I asked her to coffee. We've been together ever since, raising 3 children and now enjoying our 5 grandchildren. Martha loves cardinals (she says they remind her of her mother), classical music, and still keeps a beautiful garden. Our song has always been 'Can't Help Falling in Love' by Elvis."
            </div>
            <div className={styles.characterCount}>
              {formData.songStory.length}/3000
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="keyPoints" className={styles.label}>
              Key points or phrases you'd like included
            </label>
            <textarea
              id="keyPoints"
              name="keyPoints"
              className={styles.textarea}
              placeholder="Phrases, names, details"
              value={formData.keyPoints}
              onChange={handleInputChange}
              onBlur={handleBlur}
              rows={3}
            />
            <div className={styles.helpText}>
              List specific details like names, dates, places, or phrases that are significant
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="songTone" className={styles.label}>
              Song tone <span className={styles.requiredStar}>*</span>
            </label>
            <select
              id="songTone"
              name="songTone"
              className={styles.select}
              value={formData.songTone}
              onChange={handleInputChange}
              onBlur={handleBlur}
            >
              <option value="">Pick a song tone</option>
              <option value="romantic">Romantic</option>
              <option value="celebratory">Celebratory</option>
              <option value="nostalgic">Nostalgic</option>
              <option value="humorous">Humorous</option>
              <option value="inspirational">Inspirational</option>
              <option value="reflective">Reflective</option>
              <option value="upbeat">Upbeat</option>
              <option value="emotional">Emotional</option>
              <option value="playful">Playful</option>
              <option value="heartfelt">Heartfelt</option>
              <option value="sentimental">Sentimental</option>
              <option value="joyful">Joyful</option>
              <option value="passionate">Passionate</option>
              <option value="optimistic">Optimistic</option>
              <option value="melancholic">Melancholic</option>
              <option value="peaceful">Peaceful</option>
              <option value="dramatic">Dramatic</option>
              <option value="grateful">Grateful</option>
              <option value="energetic">Energetic</option>
              <option value="hopeful">Hopeful</option>
              <option value="whimsical">Whimsical</option>
              <option value="triumphant">Triumphant</option>
              <option value="loving">Loving</option>
              <option value="thankful">Thankful</option>
              <option value="serene">Serene</option>
              <option value="sincere">Sincere</option>
              <option value="tender">Tender</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Song Preferences</h2>
          <div className={styles.formGroup}>
            <label className={styles.label}>Vocal preference <span className={styles.requiredStar}>*</span></label>
            <div className={styles.vocalOptions}>
              <label className={`${styles.vocalOption} ${formData.vocalPreference === 'male' ? styles.selected : ''}`}>
                <input
                  type="radio"
                  name="vocalPreference"
                  value="male"
                  checked={formData.vocalPreference === 'male'}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={styles.hiddenRadio}
                />
                Male Vocalist
              </label>
              <label className={`${styles.vocalOption} ${formData.vocalPreference === 'female' ? styles.selected : ''}`}>
                <input
                  type="radio"
                  name="vocalPreference"
                  value="female"
                  checked={formData.vocalPreference === 'female'}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={styles.hiddenRadio}
                />
                Female Vocalist
              </label>
              <label className={`${styles.vocalOption} ${formData.vocalPreference === 'duet' ? styles.selected : ''}`}>
                <input
                  type="radio"
                  name="vocalPreference"
                  value="duet"
                  checked={formData.vocalPreference === 'duet'}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={styles.hiddenRadio}
                />
                Duet
              </label>
              <label className={`${styles.vocalOption} ${formData.vocalPreference === 'no-preference' ? styles.selected : ''}`}>
                <input
                  type="radio"
                  name="vocalPreference"
                  value="no-preference"
                  checked={formData.vocalPreference === 'no-preference'}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={styles.hiddenRadio}
                />
                No Preference
              </label>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="favoriteArtists" className={styles.label}>
              Favorite artists?
            </label>
            <input
              type="text"
              id="favoriteArtists"
              name="favoriteArtists"
              className={styles.input}
              placeholder="Songs or artists for inspiration"
              value={formData.favoriteArtists}
              onChange={handleInputChange}
              onBlur={handleBlur}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="additionalNotes" className={styles.label}>
              Anything else?
            </label>
            <textarea
              id="additionalNotes"
              name="additionalNotes"
              className={styles.textarea}
              placeholder="Extra details for your song"
              value={formData.additionalNotes}
              onChange={handleInputChange}
              onBlur={handleBlur}
              rows={3}
            />
          </div>
        </div>

        <div className={styles.navigation}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate('/genres')}
          >
            Back to Styles
          </button>
          <button
            type="submit"
            className={styles.nextButton}
            disabled={!isFormComplete}
            onClick={(e) => {
              if (isFormComplete) handleSubmit(e); // Fallback for Safari
            }}
          >
            Continue to Payment
          </button>
        </div>
      </form>

      <div className={styles.tips}>
        <h3 className={styles.tipsTitle}>Tips for a Great Song</h3>
        <ul className={styles.tipsList}>
          <li>Be specific—details make it unique!</li>
          <li>Add nicknames or quirks</li>
          <li>Mention special places or dates</li>
          <li>Share what you love about them</li>
          <li>Write naturally—no polish needed</li>
        </ul>
      </div>

      {showNotification && (
        <div className={styles.notification}>
          {!isFormComplete ? (
            <p>
              Fill out your song details! <br />
              {!hasRequiredFields && !hasSongPreferences ? (
                'Complete all required fields and preferences.'
              ) : !hasRequiredFields ? (
                'Complete all required fields (Occasion, Recipient, Story).'
              ) : (
                'Select a song tone and vocal preference.'
              )}
              <span style={{ color: '#F58800', fontWeight: 'bold', display: 'inline-block' }}>*</span>
            </p>
          ) : (
            <p>
              Your song is ready—proceed to payment!
            </p>
          )}
          {!hasRequiredFields ? (
            <button
              className={styles.notificationButton}
              onClick={() => {
                const songStoryElement = document.getElementById('songStory');
                if (songStoryElement) {
                  songStoryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  songStoryElement.focus();
                }
              }}
            >
              Go to Story
            </button>
          ) : !hasSongPreferences ? (
            <button
              className={styles.notificationButton}
              onClick={() => {
                const songToneElement = document.getElementById('songTone');
                if (songToneElement) {
                  songToneElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  songToneElement.focus();
                }
              }}
            >
              Set Preferences
            </button>
          ) : (
            <button
              type="submit"
              form="detailsForm"
              className={styles.notificationButton}
              onClick={(e) => {
                if (isFormComplete) handleSubmit(e); // Fallback for Safari
              }}
            >
              Continue to Payment
            </button>
          )}
          <button
            className={styles.notificationCloseButton}
            onClick={() => setShowNotification(false)}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

export default DetailsPage;